'use client';

import { c as _c } from "react/compiler-runtime";
import { formatRelativeTime, getAriaLabel } from '@bridge/utils';
import { FlexContainer, FormControl, Input } from '../../components';
import { Body } from '../../typography';
import { FieldShell } from './FieldShell';
import { getFieldIds } from './utils';
import type { FieldValues } from 'react-hook-form';
import type { BaseComponentProps, BaseFieldProps } from './types';

/** Minute precision only — `step` is excluded because values format to `YYYY-MM-DDTHH:mm`, so sub-minute input would snap back on re-render. */
export type DateTimeLocalComponentProps = Omit<BaseComponentProps<typeof Input>, 'step'> & {
  /**
   * When true, renders a relative-time hint below the input (e.g. "in 3 hours") for the
   * currently-selected value. Hidden while the value is empty or in the past.
   */
  relativeHint?: boolean;
};
interface DateTimeLocalFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: DateTimeLocalComponentProps;
}
function toDatetimeLocalValue(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
export function DateTimeLocalField(t0) {
  const $ = _c(52);
  const {
    field,
    fieldConfig,
    fieldState,
    componentProps,
    labelSize
  } = t0;
  let componentDisabled;
  let id;
  let relativeHint;
  let restComponentProps;
  if ($[0] !== componentProps) {
    ({
      id,
      disabled: componentDisabled,
      relativeHint,
      ...restComponentProps
    } = componentProps);
    $[0] = componentProps;
    $[1] = componentDisabled;
    $[2] = id;
    $[3] = relativeHint;
    $[4] = restComponentProps;
  } else {
    componentDisabled = $[1];
    id = $[2];
    relativeHint = $[3];
    restComponentProps = $[4];
  }
  const isDisabled = field.disabled || componentDisabled;
  let t1;
  if ($[5] !== fieldConfig || $[6] !== fieldState || $[7] !== id) {
    t1 = getFieldIds(fieldConfig, fieldState, id);
    $[5] = fieldConfig;
    $[6] = fieldState;
    $[7] = id;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const ids = t1;
  let t2;
  if ($[9] !== field.value || $[10] !== relativeHint) {
    t2 = relativeHint && field.value ? formatRelativeTime(field.value) : null;
    $[9] = field.value;
    $[10] = relativeHint;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const hint = t2;
  const hintId = `${ids.fieldId}-hint`;
  let t3;
  if ($[12] !== hint || $[13] !== hintId || $[14] !== ids.describedBy) {
    t3 = hint ? [ids.describedBy, hintId].filter(Boolean).join(" ") : ids.describedBy;
    $[12] = hint;
    $[13] = hintId;
    $[14] = ids.describedBy;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const describedBy = t3;
  const t4 = ids.isInvalid;
  let t5;
  if ($[16] !== fieldConfig.label) {
    t5 = getAriaLabel(fieldConfig.label);
    $[16] = fieldConfig.label;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== field) {
    t6 = event => {
      const value = event.target.value;
      field.onChange(value ? new Date(value).toISOString() : "");
    };
    $[18] = field;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== field.value) {
    t7 = field.value ? toDatetimeLocalValue(field.value) : "";
    $[20] = field.value;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== describedBy || $[23] !== field || $[24] !== ids.fieldId || $[25] !== ids.isInvalid || $[26] !== isDisabled || $[27] !== restComponentProps || $[28] !== t5 || $[29] !== t6 || $[30] !== t7) {
    t8 = <Input {...field} {...restComponentProps} aria-describedby={describedBy} aria-invalid={t4} aria-label={t5} disabled={isDisabled} id={ids.fieldId} onChange={t6} type="datetime-local" value={t7} />;
    $[22] = describedBy;
    $[23] = field;
    $[24] = ids.fieldId;
    $[25] = ids.isInvalid;
    $[26] = isDisabled;
    $[27] = restComponentProps;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const input = t8;
  if (!relativeHint) {
    let t9;
    if ($[32] !== fieldConfig || $[33] !== fieldState || $[34] !== ids || $[35] !== input || $[36] !== labelSize) {
      t9 = <FieldShell fieldConfig={fieldConfig} fieldState={fieldState} ids={ids} labelSize={labelSize}>{input}</FieldShell>;
      $[32] = fieldConfig;
      $[33] = fieldState;
      $[34] = ids;
      $[35] = input;
      $[36] = labelSize;
      $[37] = t9;
    } else {
      t9 = $[37];
    }
    return t9;
  }
  let t9;
  if ($[38] !== input) {
    t9 = <FormControl>{input}</FormControl>;
    $[38] = input;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  const t10 = hint ?? "\xA0";
  let t11;
  if ($[40] !== hintId || $[41] !== t10) {
    t11 = <Body className="text-content-secondary" id={hintId} size="xs">{t10}</Body>;
    $[40] = hintId;
    $[41] = t10;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== t11 || $[44] !== t9) {
    t12 = <FlexContainer className="gap-1" direction="column">{t9}{t11}</FlexContainer>;
    $[43] = t11;
    $[44] = t9;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== fieldConfig || $[47] !== fieldState || $[48] !== ids || $[49] !== labelSize || $[50] !== t12) {
    t13 = <FieldShell fieldConfig={fieldConfig} fieldState={fieldState} ids={ids} labelSize={labelSize} withFormControl={false}>{t12}</FieldShell>;
    $[46] = fieldConfig;
    $[47] = fieldState;
    $[48] = ids;
    $[49] = labelSize;
    $[50] = t12;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
}